import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Button,
  Grid,
  Alert,
  CircularProgress,
  IconButton,
  Tooltip,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Snackbar,
  Autocomplete,
  Tabs,
  Tab,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  MenuList,
  ClickAwayListener,
  Popper,
} from "@mui/material";
import {
  Save as SaveIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  CalendarToday as CalendarIcon,
  AccessTime as TimeIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Assignment as AssignmentIcon,
  QuestionAnswer as HelpIcon,
} from "@mui/icons-material";
import { useParams, useLocation } from 'react-router-dom';
import { decryptText, decryptAppId } from '../../../utils/encryption';
import UnfreezeApiService from '../../../services/UnfreezeApiService';
import {
    getDANC,
    insertDANC,
    updateDANCBySourceId,
    bulkUpsertDANC
} from '../../../services/apiCalibration';
import {getScrutinyRemarks, getClauseNo} from '../../../services/api';

// Adequacy Check Popup Component
const AdequacyCheckPopup = React.memo(
  ({
    open,
    onClose,
    instrumentId,
    instrumentName,
    adequacyCheck,
    onAdequacyChange,
    onRemarksChange,
    onSubmit,
    loading,
    clauseNo,
    selectedClause,
    onClauseChange,
    userTypeId,
  }) => {
    const adequacy = adequacyCheck || { status: "", remarks: "" };
    
    // Scrutiny remarks state
    const [scrutinyRemarks, setScrutinyRemarks] = useState([]);
    const [loadingRemarks, setLoadingRemarks] = useState(false);

    // Fetch scrutiny remarks when dialog opens
    useEffect(() => {
        if (open) {
            fetchScrutinyRemarks();
        }
    }, [open]);

   
 
    const fetchScrutinyRemarks = async () => {
        setLoadingRemarks(true);
        try {
            const responseApi = await getScrutinyRemarks({ 
                sectionName: 'Non-Analytical Instruments', 
                type: localStorage.getItem('userTypeId') === '6' ? 'Assessment' : 'Document Adequacy' 
            });
            const response = responseApi.data;
            if (response.success && response.data) {
                setScrutinyRemarks(response.data);
                console.log('NonAnalytical: Loaded scrutiny remarks:', response.data.length);
            } else {
                setScrutinyRemarks([]);
            }
        } catch (error) {
            console.error('Error fetching scrutiny remarks:', error);
            setScrutinyRemarks([]);
        } finally {
            setLoadingRemarks(false);
        }
    };

    const handleRemarksChange = (newValue) => {
        onRemarksChange(newValue);
    };

    return (
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: { borderRadius: 3, boxShadow: "0 20px 40px rgba(0,0,0,0.1)" },
        }}
      >
        <DialogTitle
          sx={{
            background: "linear-gradient(135deg, #007bff 0%, #0056b3 100%)",
            color: "white",
            fontWeight: 600,
            fontSize: "1.25rem",
          }}
        >
          📋 Adequacy Check - {instrumentName}
        </DialogTitle>

        <DialogContent sx={{ p: 3, mt: 2 }}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <FormControl
                fullWidth
                sx={{
                  "& .MuiOutlinedInput-root": {
                    backgroundColor: "white",
                    borderRadius: 2,
                    "&:hover": {
                      boxShadow: "0 2px 8px rgba(0,123,255,0.15)",
                    },
                    "&.Mui-focused": {
                      boxShadow: "0 4px 12px rgba(0,123,255,0.25)",
                    },
                  },
                }}
              >
                <InputLabel>Document Status</InputLabel>
                <Select
                  value={adequacy.status}
                  onChange={(e) => onAdequacyChange(e.target.value)}
                  label="Document Status"
                >
                  <MenuItem value="">-- Select Status --</MenuItem>
                  <MenuItem value="Adequate">
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Box
                        sx={{
                          width: 8,
                          height: 8,
                          borderRadius: "50%",
                          bgcolor: "#28a745",
                        }}
                      />
                      Adequate
                    </Box>
                  </MenuItem>
                  <MenuItem value="Inadequate">
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Box
                        sx={{
                          width: 8,
                          height: 8,
                          borderRadius: "50%",
                          bgcolor: "#dc3545",
                        }}
                      />
                      Inadequate
                    </Box>
                  </MenuItem>
                </Select>
              </FormControl>
            </Grid>

            {(adequacy.status === 'Inadequate' && userTypeId === '6') && (
              <Grid item xs={12} md={6}>
                <Autocomplete
                  fullWidth
                  options={clauseNo}
                  value={selectedClause}
                  onChange={(event, newValue) => {
                    onClauseChange(newValue);
                  }}
                  getOptionLabel={(option) => {
                    if (!option) return '';
                    if (typeof option === 'string') return option;
                    return option.DisplayName || option.clause_no || option.text || '';
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Clause No."
                      variant="outlined"
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          backgroundColor: 'white',
                          borderRadius: 2,
                          '&:hover': {
                            boxShadow: '0 2px 8px rgba(0,123,255,0.15)',
                          },
                          '&.Mui-focused': {
                            boxShadow: '0 4px 12px rgba(0,123,255,0.25)',
                          },
                        },
                      }}
                    />
                  )}
                  sx={{ mb: 2 }}
                />
              </Grid>
            )}

            {adequacy.status === "Inadequate" && (
              <Grid item xs={12}>
                <Autocomplete
                  fullWidth
                  freeSolo
                  loading={loadingRemarks}
                  options={scrutinyRemarks}
                  value={adequacy.remarks || ''}
                  getOptionLabel={(option) => {
                    if (typeof option === 'string') return option;
                    return option?.inadequate_remarks || '';
                  }}
                  onChange={(event, newValue) => {
                    let remarkText = '';
                    if (typeof newValue === 'string') {
                      // User typed text
                      remarkText = newValue;
                    } else if (newValue && newValue.inadequate_remarks) {
                      // User selected from dropdown
                      remarkText = newValue.inadequate_remarks;
                    }
                    handleRemarksChange(remarkText);
                  }}
                  onInputChange={(event, newInputValue) => {
                    // Handle typing in the input field
                    handleRemarksChange(newInputValue);
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      multiline
                      rows={3}
                      label={`Remarks (Required)${scrutinyRemarks.length > 0 ? ` - ${scrutinyRemarks.length} suggestions` : ''}`}
                      placeholder={scrutinyRemarks.length > 0 ? 
                        "Select from dropdown or type your own remarks..." : 
                        "Please type your remarks here."
                      }
                      required
                      variant="outlined"
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          backgroundColor: 'white',
                          borderRadius: 2,
                          '&:hover': {
                            boxShadow: '0 2px 8px rgba(220,53,69,0.15)',
                          },
                          '&.Mui-focused': {
                            boxShadow: '0 4px 12px rgba(220,53,69,0.25)',
                          },
                        },
                      }}
                    />
                  )}
                />
              </Grid>
            )}
          </Grid>

          {adequacy.status && (
            <Box sx={{ mt: 3 }}>
              <Alert
                severity={
                  adequacy.isSubmitted
                    ? "info"
                    : adequacy.status === "Adequate"
                    ? "success"
                    : "warning"
                }
                sx={{ borderRadius: 2 }}
              >
                {adequacy.isSubmitted
                  ? `✅ Previously submitted on ${new Date(
                      adequacy.submittedAt
                    ).toLocaleString()}. You can update this adequacy check.`
                  : adequacy.status === "Adequate"
                  ? "✓ This instrument is marked as Adequate."
                  : "⚠ Please address the remarks above."}
              </Alert>
            </Box>
          )}
        </DialogContent>

        <DialogActions sx={{ p: 3, pt: 0 }}>
          <Button onClick={onClose} sx={{ color: "#6c757d" }}>
            Cancel
          </Button>
          {adequacy.status && (
            <Button
              variant="contained"
              onClick={onSubmit}
              disabled={
                loading ||
                (adequacy.status === "Inadequate" && !adequacy.remarks?.trim())
              }
              sx={{
                background:
                  adequacy.status === "Adequate"
                    ? "linear-gradient(135deg, #28a745 0%, #20c997 100%)"
                    : "linear-gradient(135deg, #ffc107 0%, #fd7e14 100%)",
                color: "white",
                fontWeight: 600,
                "&:hover": {
                  transform: "translateY(-1px)",
                  boxShadow: "0 4px 12px rgba(0,0,0,0.2)",
                },
              }}
            >
              {loading ? (
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <CircularProgress size={16} sx={{ color: "white" }} />
                  Submitting...
                </Box>
              ) : adequacy.isSubmitted ? (
                "🔄 Update Adequacy Check"
              ) : (
                "📤 Submit Adequacy Check"
              )}
            </Button>
          )}
        </DialogActions>
      </Dialog>
    );
  }
);

const NonAnalyticalInstrumentsSection = () => {
  const { orgId: urlOrgId } = useParams();
  const location = useLocation();
  
  // Function to get orgId from URL query parameters with decryption
  const getOrgIdFromQuery = useCallback(() => {
    const searchParams = new URLSearchParams(location.search);
    const encryptedOrgId = searchParams.get('orgId');
    if (encryptedOrgId) {
      try {
        const decryptedOrgId = decryptText(encryptedOrgId);
        console.log('📍 NonAnalyticalInstrumentsSection - Decrypted orgId from URL query:', decryptedOrgId);
        console.log('📍 NonAnalyticalInstrumentsSection - Original encrypted orgId:', encryptedOrgId);
        return decryptedOrgId;
      } catch (error) {
        console.log('⚠️ NonAnalyticalInstrumentsSection - Failed to decrypt orgId, using original:', encryptedOrgId, error);
        return encryptedOrgId; // Return original if decryption fails
      }
    }
    return null;
  }, [location.search]);

  const userTypeId = localStorage.getItem('userTypeId');

  // Function to get assessmentId from URL query parameters with decryption
  const getAssessmentIdFromQuery = () => {
    const searchParams = new URLSearchParams(location.search);
    const encryptedAssessmentId = searchParams.get('assessmentId');
    if (encryptedAssessmentId) {
      try {
        const decryptedAssessmentId = decryptText(encryptedAssessmentId);
        console.log('📍 NonAnalyticalInstrumentsSection - Decrypted assessmentId from URL query:', decryptedAssessmentId);
        return decryptedAssessmentId;
      } catch (error) {
        console.log('⚠️ NonAnalyticalInstrumentsSection - Failed to decrypt assessmentId:', error);
        return encryptedAssessmentId;
      }
    }
    return null;
  };

  // Function to get assessorId from URL query parameters with decryption
  const getAssessorIdFromQuery = () => {
    const searchParams = new URLSearchParams(location.search);
    const encryptedAssessorId = searchParams.get('assessorId');
    if (encryptedAssessorId) {
      try {
        const decryptedAssessorId = decryptText(encryptedAssessorId);
        console.log('📍 NonAnalyticalInstrumentsSection - Decrypted assessorId from URL query:', decryptedAssessorId);
        return decryptedAssessorId;
      } catch (error) {
        console.log('⚠️ NonAnalyticalInstrumentsSection - Failed to decrypt assessorId:', error);
        return encryptedAssessorId;
      }
    }
    return null;
  };

  const getAssessmentId = () => {
    return getAssessmentIdFromQuery();
  };

  const getAssessorId = () => {
    return getAssessorIdFromQuery();
  };

  // Get assessmentId and assessorId for userType 6 (Assessment)
  const assessmentId = getAssessmentId();
  const assessorId = getAssessorId();

  // Function to get appId from URL query parameters with decryption
  const getAppIdFromQuery = useCallback(() => {
    const searchParams = new URLSearchParams(location.search);
    const encryptedAppId = searchParams.get('appId');
    if (encryptedAppId) {
      try {
        const decryptedAppId = decryptAppId(encryptedAppId);
        console.log('📍 NonAnalyticalInstrumentsSection - Decrypted appId from URL query:', decryptedAppId);
        console.log('📍 NonAnalyticalInstrumentsSection - Original encrypted appId:', encryptedAppId);
        return decryptedAppId;
      } catch (error) {
        console.log('⚠️ NonAnalyticalInstrumentsSection - Failed to decrypt appId, using original:', encryptedAppId, error);
        return encryptedAppId; // Return original if decryption fails
      }
    }
    return null;
  }, [location.search]);

  // Function to get orgId with priority: URL query (encrypted) > URL params > null
  const getOrgId = useCallback(() => {
    // First check URL query parameters (encrypted) - highest priority
    const queryOrgId = getOrgIdFromQuery();
    if (queryOrgId) {
      console.log('📍 NonAnalyticalInstrumentsSection - Using decrypted orgId from URL query:', queryOrgId);
      return queryOrgId;
    }

    // Then check URL parameters (unencrypted)
    if (urlOrgId) {
      console.log('📍 NonAnalyticalInstrumentsSection - Using orgId from URL params:', urlOrgId);
      return urlOrgId;
    }

    // No fallback - orgId must be provided via URL
    console.log('⚠️ NonAnalyticalInstrumentsSection - No orgId found in URL parameters');
    return null;
  }, [getOrgIdFromQuery, urlOrgId]);

  // Function to get appId with priority: URL query (encrypted) only
  const getAppId = useCallback(() => {
    // Only check URL query parameters (encrypted)
    const queryAppId = getAppIdFromQuery();
    if (queryAppId) {
      console.log('📍 NonAnalyticalInstrumentsSection - Using decrypted appId from URL query:', queryAppId);
      return queryAppId;
    }

    // No fallback - appId must be provided via URL query parameters
    console.log('⚠️ NonAnalyticalInstrumentsSection - No appId found in URL query parameters');
    return null;
  }, [getAppIdFromQuery]);

  const [orgId, setOrgId] = useState(() => {
    // Initialize with query parameters first, then URL params
    const searchParams = new URLSearchParams(location.search);
    const encryptedOrgId = searchParams.get('orgId');
    if (encryptedOrgId) {
      try {
        const decryptedOrgId = decryptText(encryptedOrgId);
        console.log('🚀 NonAnalyticalInstrumentsSection - Initial orgId from encrypted query:', decryptedOrgId);
        return decryptedOrgId;
      } catch (error) {
        console.log('Failed to decrypt initial orgId, using original:', encryptedOrgId);
        return encryptedOrgId;
      }
    }
    // Only fallback to URL params, no localStorage
    return urlOrgId || null;
  });
  
  const [appId, setAppId] = useState(() => {
    // Initialize with query parameters only
    const searchParams = new URLSearchParams(location.search);
    const encryptedAppId = searchParams.get('appId');
    if (encryptedAppId) {
      try {
        const decryptedAppId = decryptAppId(encryptedAppId);
        console.log('🚀 NonAnalyticalInstrumentsSection - Initial appId from encrypted query:', decryptedAppId);
        return decryptedAppId;
      } catch (error) {
        console.log('Failed to decrypt initial appId, using original:', encryptedAppId);
        return encryptedAppId;
      }
    }
    // No fallback - must be provided via query parameters
    return null;
  });

  // Update orgId state only (no localStorage)
  const updateOrgId = (newOrgId) => {
    console.log('Updating orgId state:', newOrgId);
    setOrgId(newOrgId);
  };

  // Update appId state only (no localStorage)
  const updateAppId = (newAppId) => {
    console.log('Updating appId state:', newAppId);
    setAppId(newAppId);
  };

  // useEffect to handle parameter updates from URL
  useEffect(() => {
    const currentOrgId = getOrgId();
    if (currentOrgId !== orgId) {
      updateOrgId(currentOrgId);
    }

    const currentAppId = getAppId();
    if (currentAppId !== appId) {
      console.log('NonAnalyticalInstrumentsSection - AppId from URL query params:', currentAppId);
      updateAppId(currentAppId);
    }
  }, [urlOrgId, location.search, orgId, appId, getOrgId, getAppId]);

  const [instruments, setInstruments] = useState([]);
  const [existingInstruments, setExistingInstruments] = useState([]);
  const [editId, setEditId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  // New states for adding instruments
  const [selectedInstrument, setSelectedInstrument] = useState("");
  const [quantity, setQuantity] = useState("");

  // State for available instruments from API
  const [availableInstruments, setAvailableInstruments] = useState([]);

  // For existing (saved) instruments
  const [deleteExistingDialogOpen, setDeleteExistingDialogOpen] =
    useState(false);
  const [existingInstrumentToDelete, setExistingInstrumentToDelete] =
    useState(null);

  // Application stage state
  const [applicationStage, setApplicationStage] = useState(null);

  // Adequacy Check States
  const [adequacyChecks, setAdequacyChecks] = useState({});
  const [adequacyModalOpen, setAdequacyModalOpen] = useState(false);
  const [currentAdequacyInstrumentId, setCurrentAdequacyInstrumentId] = useState(null);
  const [currentAdequacyInstrumentName, setCurrentAdequacyInstrumentName] = useState("");
  const [adequacyLoading, setAdequacyLoading] = useState(false);
  const [dancRecords, setDancRecords] = useState([]);
  
  // Unfreeze functionality
  const [unfreezeRecords, setUnfreezeRecords] = useState([]);
  
  // Tab state for switching between Add New and Existing
  // Initialize to show "Existing" tab by default
  const [tabValue, setTabValue] = useState(() => {
    return 1;
  });
  
  // Bulk Adequacy Check States
  const [bulkAdequacyModalOpen, setBulkAdequacyModalOpen] = useState(false);
  const [selectedAdequacyInstruments, setSelectedAdequacyInstruments] = useState([]);
  const [bulkAdequacyStatus, setBulkAdequacyStatus] = useState("");
  const [bulkAdequacyRemarks, setBulkAdequacyRemarks] = useState("");
  const [bulkAdequacyLoading, setBulkAdequacyLoading] = useState(false);
  
  // Bulk Adequacy Scrutiny Remarks States
  const [bulkScrutinyRemarks, setBulkScrutinyRemarks] = useState([]);
  const [bulkLoadingRemarks, setBulkLoadingRemarks] = useState(false);

  // Checkbox dropdown states for bulk adequacy
  const [selectedBulkRemarksOptions, setSelectedBulkRemarksOptions] = useState([]);
  const [isBulkCheckboxDropdownOpen, setIsBulkCheckboxDropdownOpen] = useState(false);

  // Clause Number states
  const [clauseNo, setClauseNo] = useState([]);
  const [selectedClause, setSelectedClause] = useState(null);
  const [bulkSelectedClause, setBulkSelectedClause] = useState(null);

  // Current user for DA NC system
  const [currentUserId] = useState(() => {
    return parseInt(localStorage.getItem("currentUserId")) || 0;
  });

  // Function to fetch master instruments from API
  const fetchMasterInstruments = async () => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/non-analytical-instruments/master-data`
      );

      if (response.data.success) {
        // Extract instrument names from API response
        console.log("API Response data:", response.data.data);
        const instrumentsData = response.data.data;
        setAvailableInstruments(instrumentsData);
      } else {
        setError("Failed to load available instruments");
        // Fallback to default instruments
        setAvailableInstruments([]);
      }
    } catch (error) {
      console.error("Error fetching available instruments:", error);
      setError("Failed to load available instruments");
      // Fallback to default instruments
      setAvailableInstruments([]);
    }
  };

  const fetchExistingInstruments = useCallback(async () => {
    const currentAppId = getAppId();
    const currentOrgId = getOrgId();
    console.log('🔍 NonAnalyticalInstrumentsSection - fetchExistingInstruments using orgId:', currentOrgId, 'appId:', currentAppId);
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/non-analytical-instruments/${currentAppId}/${currentOrgId}/all`
      );

      if (response.data.success) {
        const instrumentsData = response.data.data;
        setExistingInstruments(instrumentsData);
      } else {
        setError("Failed to load existing instruments");
        setExistingInstruments([]);
      }
    } catch (error) {
      console.error("Error fetching existing instruments:", error);
      setError("Failed to load existing instruments");
      setExistingInstruments([]);
    }
  }, [getAppId, getOrgId]);
  // Get application stage from LabApplications table
  const getApplicationStage = useCallback(async () => {
    try {
      const currentAppId = getAppId();
      console.log('🔍 NonAnalyticalInstrumentsSection - getApplicationStage using appId:', currentAppId);
      if (!currentAppId) return;

      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/laboratory/application-stage/${currentAppId}`
      );

      if (response.data.success) {
        console.log('🔍 NonAnalytical - Application stage fetched:', response.data.stage);
        setApplicationStage(response.data.stage);
      } else {
        console.log("Stage not found");
        setApplicationStage(null);
      }
    } catch (error) {
      console.error("Error fetching application stage:", error);
      setApplicationStage(null);
    }
  }, [getAppId]);

   // Fetch clause numbers from API
     const fetchClauseNo = useCallback(async () => {
         try {
             console.log('Fetching clause numbers for Lab Staff section...');
 
             const clauseResponse = await getClauseNo({ standard: '2017', feild: 'Calibration' });
 
             if (clauseResponse.data?.success && clauseResponse.data?.data) {
                 setClauseNo(clauseResponse.data.data);
                 console.log('Loaded clause numbers:', clauseResponse.data.data.length);
             } else {
                 console.warn('No clause numbers found or API returned success: false');
                 setClauseNo([]);
             }
         } catch (error) {
             console.error('Error fetching clause numbers:', error);
             setClauseNo([]);
         }
     }, []);

  // Load DA-NC data function
  const loadDANCData = useCallback(async () => {
    const currentAppId = getAppId();
    const currentOrgId = getOrgId();
    console.log('🔍 NonAnalyticalInstrumentsSection - loadDANCData using orgId:', currentOrgId, 'appId:', currentAppId);
    
    if (!currentOrgId || !currentAppId) {
      return;
    }

    try {
      // Determine expected NCtype based on userTypeId
      const userTypeId = localStorage.getItem('userTypeId');
      let expectedNCType = 'Document Adequacy'; // Default
      
      if (userTypeId === '4') {
        expectedNCType = 'Document Adequacy';
      } else if (userTypeId === '6') {
        expectedNCType = 'Assessment';
      }
      
      console.log(`🔍 NonAnalyticalInstrumentsSection - Loading DANC data for userType ${userTypeId} with expected NCtype: ${expectedNCType}`);
      
      // Get all DA NC records for this org and app
      const response = await getDANC({
        orgid: currentOrgId,
        appid: currentAppId,
        assessorId: assessorId,
        isactive: 1,
      });

      if (response.data.success && response.data.data && response.data.data.length > 0) {
        console.log(`📋 Found ${response.data.data.length} total records for NonAnalytical section`);
        
        // Filter Records for Non-Analytical section and by NCtype
        const nonAnalyticalDANCRecords = response.data.data.filter(
          (record) =>
            (record.SectionName === 'Non-Analytical Instruments' ||
            record.SectionName === 'NonAnalytical Instruments' ||
            record.SectionName === 'Non Analytical Instruments') &&
            record.NCtype === expectedNCType
        );
        
        console.log(`🎯 Filtered to ${nonAnalyticalDANCRecords.length} non-analytical records with NCtype: ${expectedNCType}`);
        
        setDancRecords(nonAnalyticalDANCRecords);
        
        console.log("🔍 Loading DA-NC data:", nonAnalyticalDANCRecords);
        
        // Build adequacy checks from Records
        const checks = {};
        nonAnalyticalDANCRecords.forEach((record) => {
          if (record.sourceid) {
            const sourceId = String(record.sourceid); // Ensure sourceid is treated as string
            checks[sourceId] = {
              status: record.InformationStatus || "",
              remarks: record.Observation || "",
              ClauseNo: record.ClauseNo || '',
              isSubmitted: true,
              submittedAt: record.Insertdate,
            };
            
            console.log(`📝 Mapped Record for sourceid ${sourceId}:`, {
              status: record.InformationStatus,
              remarks: record.Observation,
              isSubmitted: true,
              submittedAt: record.Insertdate
            });
          }
        });
        
        console.log("🎯 Final adequacy checks object:", checks);
        setAdequacyChecks(checks);
      }
    } catch (error) {
      console.error("Error loading DA-NC data:", error);
    }
  }, [getAppId, getOrgId]);


  // Adjust tab index based on applicationStage once it's loaded
  useEffect(() => {
    const requestedTab = sessionStorage.getItem('nonanalytical_requested_tab');
    if (requestedTab !== null && applicationStage !== null) {
      const tabIndex = parseInt(requestedTab);
      
      // Map the original tab indices to the new indices based on stage
      let adjustedTab = tabIndex;
      
      // When stage is 18/""/null/"18a", tabs are: 0=Add New, 1=Existing (both tabs visible)
      // When stage is other (not null/""/"18"/"18a"), tabs are: 0=Existing (only 1 tab visible)
      
      if (applicationStage !== null && applicationStage !== 'null' && applicationStage !== '' && applicationStage !== '18' && applicationStage !== '18a') {
        // When stage is not null/""/"18"/"18a", tabs are: 0=Existing
        // Original tabs were: 0=Add New, 1=Existing
        if (tabIndex === 0) {
          // Add New doesn't exist when stage is not null/""/"18"/"18a", go to Existing
          adjustedTab = 0;
        }
        // tabIndex 1 (Existing) stays at 0
        else if (tabIndex === 1) {
          adjustedTab = 0;
        }
      }
      // For null/""/"18"/"18a", both tabs are visible so no adjustment needed
      
      setTabValue(adjustedTab);
      sessionStorage.removeItem('nonanalytical_requested_tab');
      
      console.log('🔄 Adjusted non-analytical instrument tab index:', {
        requested: tabIndex,
        adjusted: adjustedTab,
        applicationStage
      });
    }
  }, [applicationStage]);

  const handleSaveToDatabase = async () => {
    const currentAppId = getAppId();
    const currentOrgId = getOrgId();
    console.log('🔍 NonAnalyticalInstrumentsSection - handleSaveToDatabase using orgId:', currentOrgId, 'appId:', currentAppId);

    if (!instruments.length) {
      setError("No instruments to save");
      return;
    }

    // Validation: check required fields based on calibratedReq
    for (let i = 0; i < instruments.length; i++) {
      const inst = instruments[i];
      if (inst.calibratedReq === "Y") {
        if (
          !inst.uid ||
          !inst.calibrationFrequency ||
          !inst.selectedDate1 ||
          !inst.selectedDate2 ||
          !inst.calibratedBy
        ) {
          setError(
            `Instrument #${i + 1} (${
              inst.name
            }): UID, Calibration Frequency, Last Calibration Date, Next Calibration Date, and Calibrated By are required.`
          );
          return;
        }
      } else if (inst.calibratedReq === "N") {
        if (!inst.uid) {
          setError(`Instrument #${i + 1} (${inst.name}): UID is required.`);
          return;
        }
      }
    }

    setLoading(true);
    setError("");
    setSuccess("");
    try {
      // Prepare instruments array for backend
      const payload = {
        instruments: instruments.map((inst) => ({
          id: inst.id || null,
          instrumentId: inst.instrumentId || null,
          instrumentName: inst.InstrumentName || inst.name,  // Handle both name fields
          uid: inst.uid || null,
          calibrationFrequency: inst.calibrationFrequency || null,
          lastCalibrationDate: inst.selectedDate1 || null,
          nextCalibrationDate: inst.selectedDate2 || null,
          calibratedBy: inst.calibratedBy || null,
          insertedBy: parseInt(localStorage.getItem("currentUserId")) || null,
        })),
      };
      if (editId) {
        payload.instruments[0].id = editId;
      }
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/non-analytical-instruments/${currentAppId}/${currentOrgId}/bulk`,
        payload
      );

      if (response.data.success) {
        if (editId) {
          setSuccess(
            response.data.message || "Instrument data updated successfully!"
          );
        } else {
          setSuccess(
            response.data.message || "Instrument data saved successfully!"
          );
        }
        setInstruments([]);
        await fetchExistingInstruments();
        setTabValue(1);
      } else {
        setError(response.data.message || "Failed to save instrument data");
      }
    } catch (err) {
      setError(err.response?.data?.message || "Failed to save instrument data");
    } finally {
      setLoading(false);
    }
  };

  // Unfreeze utility functions
  const isSectionUnfrozen = useCallback((sectionName) => {
    const result = unfreezeRecords.some(record => {
      const isMatched = record.sectionname === sectionName;
      
      if (isMatched) {
        const expiryDate = new Date(record.expirydate);
        // Reset expiry date time to start of day for date-only comparison
        expiryDate.setHours(0, 0, 0, 0);
        
        const currentDate = new Date();
        // Reset current date time to start of day for date-only comparison
        currentDate.setHours(0, 0, 0, 0);
        
        const isNotExpired = expiryDate >= currentDate;
        return isNotExpired;
      }
      return false;
    });
    console.log('🔍 NonAnalytical isSectionUnfrozen check:', {
      sectionName,
      unfreezeRecords,
      result,
      applicationStage
    });
    return result;
  }, [unfreezeRecords, applicationStage]);

  const loadUnfreezeRecords = useCallback(async () => {
    if (!appId) return;
    try {
      const response = await UnfreezeApiService.getUnfreezeRecords(appId);
      setUnfreezeRecords(response.data || []);
    } catch (error) {
      console.error('Error loading unfreeze records:', error);
      setUnfreezeRecords([]);
    }
  }, [appId]);
    // Debug effect to monitor key state changes
  useEffect(() => {
    console.log('🔍 NonAnalytical Debug - State changed:', {
      applicationStage,
      unfreezeRecords: unfreezeRecords.length,
      appId,
      tabValue,
      existingInstruments: existingInstruments.length
    });
  }, [applicationStage, unfreezeRecords, appId, tabValue, existingInstruments]);

  // Load instruments on component mount
  useEffect(() => {
    getApplicationStage();
    fetchMasterInstruments();
    fetchExistingInstruments();
    loadDANCData();
    loadUnfreezeRecords();
    fetchClauseNo();
  }, [loadDANCData, getApplicationStage, fetchExistingInstruments, loadUnfreezeRecords, fetchClauseNo]);


  const handleInputChange = (instrumentId, field, value) => {
    setInstruments((prev) =>
      prev.map((instrument) =>
        instrument.id === instrumentId
          ? { ...instrument, [field]: value }
          : instrument
      )
    );
  };
 
  const handleCloseSnackbar = () => {
    setSuccess("");
    setError("");
  };

  const handleAddInstrument = () => {
    if (!selectedInstrument || !quantity) {
      setError("Please select an instrument and enter quantity");
      return;
    }

    const quantityNum = parseInt(quantity);
    if (quantityNum <= 0) {
      setError("Quantity must be greater than 0");
      return;
    }

    // Find the selected instrument object to get Calibrated_Req
    const selectedInstrumentObj = availableInstruments.find(
      (instrument) => instrument.InstrumentName === selectedInstrument
    );

    const calibratedReq = selectedInstrumentObj?.Calibrated_Req || "No";
    console.log("Selected instrument object:", selectedInstrumentObj);
    console.log("Calibrated_Req value:", calibratedReq);

    // Create multiple instruments based on quantity
    const newInstruments = [];
    for (let i = 0; i < quantityNum; i++) {
      newInstruments.push({
        instrumentId: selectedInstrumentObj?.id || null,
        id: Date.now() + i,
        name: selectedInstrument,
        InstrumentName: selectedInstrument,  // Add this field to match backend expectation
        calibratedReq: calibratedReq,
        uid: "",
        calibrationFrequency: "",
        selectedDate1: null,
        selectedDate2: null,
        calibratedBy: "",
      });
    }

    setInstruments((prev) => [...prev, ...newInstruments]);
    setSelectedInstrument("");
    setQuantity("");
    setSuccess(
      `${quantityNum} ${selectedInstrument}(s) added successfully! (Calibration Required: ${calibratedReq})`
    );
  };

  const handleTabChange = (event, newValue) => {
    setEditId(null);
    setTabValue(newValue);
  };

  // Delete existing instrument
  const handleDeleteExistingInstrument = (instrument) => {
    setExistingInstrumentToDelete(instrument);
    setDeleteExistingDialogOpen(true);
  };

  const confirmDeleteExisting = async () => {
    if (!existingInstrumentToDelete) return;
    setLoading(true);
    try {
      const currentAppId = getAppId();
      const currentOrgId = getOrgId();
      console.log('🔍 NonAnalyticalInstrumentsSection - confirmDeleteExisting using orgId:', currentOrgId, 'appId:', currentAppId);
      const id = existingInstrumentToDelete.id;
      const response = await axios.delete(
        `${process.env.REACT_APP_API_URL}/non-analytical-instruments/${currentAppId}/${currentOrgId}/${id}`
      );
      if (response.data.success) {
        setSuccess("Instrument deleted successfully!");
        setExistingInstruments((prev) => prev.filter((inst) => inst.id !== id));
      } else {
        setError(response.data.message || "Failed to delete instrument");
      }
    } catch (error) {
      setError("Failed to delete instrument");
    } finally {
      setLoading(false);
      setDeleteExistingDialogOpen(false);
      setExistingInstrumentToDelete(null);
    }
  };

  const cancelDeleteExisting = () => {
    setDeleteExistingDialogOpen(false);
    setExistingInstrumentToDelete(null);
  };

  // Edit existing instrument: populate Add New form
  const handleEditExistingInstrument = (instrument) => {
    setEditId(instrument.id);
    setTabValue(0);
    setInstruments([
      {
        instrumentId:
          instrument.Instrumentid || instrument.instrumentId || null,
        id: instrument.id,
        name: instrument.InstrumentName,
        InstrumentName: instrument.InstrumentName,  // Add this field to match backend expectation
        calibratedReq:
          instrument.Calibrated_Req ||
          (instrument.Calibration_Frequency ? "Y" : "N"),
        uid: instrument.UID || "",
        calibrationFrequency: instrument.Calibration_Frequency || "",
        selectedDate1: instrument.LastCalibration_Date
          ? instrument.LastCalibration_Date.split("T")[0]
          : "",
        selectedDate2: instrument.NextCalibration_Date
          ? instrument.NextCalibration_Date.split("T")[0]
          : "",
        calibratedBy: instrument.CalibratedBy || "",
      },
    ]);
    //setSelectedInstrument(instrument.InstrumentName);
    //setQuantity('1');
  };

  // Adequacy Check Handlers
  const handleOpenAdequacyModal = useCallback((instrumentId, instrumentName) => {
    const stringId = String(instrumentId);
    console.log(`🔍 Opening adequacy modal for instrument ${stringId} (${instrumentName})`);
    console.log("Current adequacy checks:", adequacyChecks);
    console.log("Adequacy check for this instrument:", adequacyChecks[stringId]);
    
    setCurrentAdequacyInstrumentId(stringId);
    setCurrentAdequacyInstrumentName(instrumentName);
    setAdequacyModalOpen(true);
  }, [adequacyChecks]);

  const handleCloseAdequacyModal = useCallback(() => {
    setAdequacyModalOpen(false);
    setCurrentAdequacyInstrumentId(null);
    setCurrentAdequacyInstrumentName("");
    setSelectedClause(null);
  }, []);

  const handleAdequacyChange = useCallback(
    (status) => {
      setAdequacyChecks((prev) => ({
        ...prev,
        [currentAdequacyInstrumentId]: {
          ...(prev[currentAdequacyInstrumentId] || {}),
          status,
          remarks: status === "Adequate" ? "" : prev[currentAdequacyInstrumentId]?.remarks || "",
          // Keep the isSubmitted flag but allow updates
          isSubmitted: prev[currentAdequacyInstrumentId]?.isSubmitted || false,
          submittedAt: prev[currentAdequacyInstrumentId]?.submittedAt || null,
        },
      }));
    },
    [currentAdequacyInstrumentId]
  );

  const handleAdequacyRemarks = useCallback(
    (remarks) => {
      setAdequacyChecks((prev) => ({
        ...prev,
        [currentAdequacyInstrumentId]: {
          ...(prev[currentAdequacyInstrumentId] || {}),
          remarks,
        },
      }));
    },
    [currentAdequacyInstrumentId]
  );

  const handleAdequacySubmit = useCallback(async () => {
    const currentAppId = getAppId();
    const currentOrgId = getOrgId();
    console.log('🔍 NonAnalyticalInstrumentsSection - handleAdequacySubmit using orgId:', currentOrgId, 'appId:', currentAppId);
    
    const adequacy = adequacyChecks[currentAdequacyInstrumentId];
    
    console.log("🔍 Adequacy Submit Debug:", {
      currentAdequacyInstrumentId,
      adequacy,
      dancRecords: dancRecords.length,
      appId: currentAppId,
      orgId: currentOrgId
    });
    
    if (!adequacy || !adequacy.status) {
      setError("Please select a document status");
      return;
    }

    if (adequacy.status === "Inadequate" && !adequacy.remarks?.trim()) {
      setError("Remarks are required when marking as Inadequate");
      return;
    }

    setAdequacyLoading(true);
    try {
      // Check if Record already exists for this instrument
      const existingRecord = dancRecords.find(
        (record) => String(record.sourceid) === String(currentAdequacyInstrumentId)
      );
      
      console.log("🔍 Existing record found:", existingRecord);
      console.log("🔍 Looking for sourceid:", currentAdequacyInstrumentId);
      console.log("🔍 Available sourceids:", dancRecords.map(r => r.sourceid));

      // Determine NCtype based on userTypeId
      const userTypeId = localStorage.getItem('userTypeId');
      let ncType = 'Document Adequacy'; // Default
      
      if (userTypeId === '4') {
        ncType = 'Document Adequacy';
      } else if (userTypeId === '6') {
        ncType = 'Assessment';
      }
      
      if (existingRecord) {
        // Update existing record using updateDANCBySourceId
        console.log(`💾 Updating DANC record for instrument ${currentAdequacyInstrumentId}, userType ${userTypeId} with NCtype: ${ncType}`);
        
        const updateData = {
          subsectionName: '',
          informationStatus: adequacy.status,
          observation: adequacy.remarks || "",
          NCtype: ncType,
          sourceid: currentAdequacyInstrumentId,
          isactive: 1,
          ClauseNo: selectedClause ? (typeof selectedClause === 'string' ? selectedClause : (selectedClause.DisplayName || selectedClause.clause_no || selectedClause.text || '')) : null,
        };
        
        console.log("🔄 Updating existing Record:", updateData);
        
        // Add assessmentId and assessorId for userType 6 (Assessment)
        let response;
        if (userTypeId === '6') {
          response = await updateDANCBySourceId(
            currentOrgId,
            currentAppId,
            'Non-Analytical Instruments',
            currentAdequacyInstrumentId,
            assessmentId,
            assessorId,
            updateData
          );
        } else {
          response = await updateDANCBySourceId(
            currentOrgId,
            currentAppId,
            'Non-Analytical Instruments',
            currentAdequacyInstrumentId,
            0,
            localStorage.getItem('orgId'),
            updateData
          );
        }
        
        if (response.success) {
          console.log("✅ Record updated successfully");
        } else {
          throw new Error(response.message || 'Failed to Update');
        }
      } else {
        // Create new record
        console.log(`✨ Creating new DANC record for instrument ${currentAdequacyInstrumentId}, userType ${userTypeId} with NCtype: ${ncType}`);
        
        const createPayload = {
          Orgid: parseInt(currentOrgId),
          Appid: parseInt(currentAppId),
          SectionName: "Non-Analytical Instruments",
          SubsectionName: '',
          InformationProvided: "",
          InformationStatus: adequacy.status,
          Observation: adequacy.remarks || "",
          Insertedby: currentUserId,
          NCtype: ncType,
          sourceid: currentAdequacyInstrumentId,
          ClauseNo: selectedClause ? (typeof selectedClause === 'string' ? selectedClause : (selectedClause.DisplayName || selectedClause.clause_no || selectedClause.text || '')) : null,
          assessmentId: parseInt(assessmentId) || 0,
          assessorId: parseInt(assessorId) || localStorage.getItem('orgId')
        };
        
        console.log("📝 Creating new Record:", createPayload);
        
        const response = await insertDANC(createPayload);
        
        if (response.success) {
          console.log("✅ Record created successfully");
        } else {
          throw new Error(response.message || 'Failed to create Record');
        }
      }

      // Update local state
      setAdequacyChecks((prev) => ({
        ...prev,
        [currentAdequacyInstrumentId]: {
          ...adequacy,
          ClauseNo: selectedClause ? (typeof selectedClause === 'string' ? selectedClause : (selectedClause.DisplayName || selectedClause.clause_no || selectedClause.text || '')) : '',
          isSubmitted: true,
          submittedAt: new Date().toISOString(),
        },
      }));

      setSuccess("Adequacy check submitted successfully!");
      handleCloseAdequacyModal();
      
      // Reload DA-NC data
      await loadDANCData();
    } catch (error) {
      console.error("Error submitting adequacy check:", error);
      setError(error.response?.data?.message || "Failed to submit adequacy check");
    } finally {
      setAdequacyLoading(false);
    }
  }, [currentAdequacyInstrumentId, adequacyChecks, dancRecords, currentUserId, handleCloseAdequacyModal, loadDANCData, getAppId, getOrgId, appId, orgId]);

  // Bulk Adequacy Check Handlers
  const handleOpenBulkAdequacyModal = () => {
    if (selectedAdequacyInstruments.length === 0) {
      setError("Please select at least one instrument for bulk adequacy check");
      return;
    }
    setBulkAdequacyModalOpen(true);
    setBulkAdequacyStatus("");
    setBulkAdequacyRemarks("");
    fetchBulkScrutinyRemarks();
  };

  const fetchBulkScrutinyRemarks = async () => {
    setBulkLoadingRemarks(true);
    try {
      const responseApi = await getScrutinyRemarks({ 
        sectionName: 'Non-Analytical Instruments', 
        type: localStorage.getItem('userTypeId') === '6' ? 'Assessment' : 'Document Adequacy' 
      });
      const response = responseApi.data;
      if (response.success && response.data) {
        setBulkScrutinyRemarks(response.data);
        console.log('Bulk NonAnalytical: Loaded scrutiny remarks:', response.data.length);
      } else {
        setBulkScrutinyRemarks([]);
      }
    } catch (error) {
      console.error('Error fetching bulk scrutiny remarks:', error);
      setBulkScrutinyRemarks([]);
    } finally {
      setBulkLoadingRemarks(false);
    }
  };

  const handleCloseBulkAdequacyModal = () => {
    setBulkAdequacyModalOpen(false);
    setBulkAdequacyStatus("");
    setBulkAdequacyRemarks("");
    setBulkScrutinyRemarks([]);
    setBulkSelectedClause(null);
    // Reset checkbox dropdown states
    setSelectedBulkRemarksOptions([]);
    setIsBulkCheckboxDropdownOpen(false);
  };

  // Checkbox dropdown handlers for bulk adequacy
  const handleBulkCheckboxChange = useCallback((option, isChecked) => {
    const optionText = typeof option === 'string' ? option : option.inadequate_remarks;
    
    if (isChecked) {
      setSelectedBulkRemarksOptions(prev => [...prev, option]);
    } else {
      setSelectedBulkRemarksOptions(prev => 
        prev.filter(item => {
          const itemText = typeof item === 'string' ? item : item.inadequate_remarks;
          return itemText !== optionText;
        })
      );
    }
  }, []);

  const handleClearAllBulkCheckboxes = useCallback(() => {
    setSelectedBulkRemarksOptions([]);
    setBulkAdequacyRemarks('');
  }, []);

  const handleBulkAdequacyRemarksChange = useCallback((newValue, isFromDropdown = false) => {
    setBulkAdequacyRemarks(newValue);
    
    if (!isFromDropdown && selectedBulkRemarksOptions.length > 0) {
      const selectedTexts = selectedBulkRemarksOptions.map(option => 
        typeof option === 'string' ? option : option.inadequate_remarks
      );
      
      if (!selectedTexts.some(text => newValue.includes(text))) {
        setSelectedBulkRemarksOptions([]);
      }
    }
  }, [selectedBulkRemarksOptions]);

  // Update remarks when checkbox selections change
  useEffect(() => {
    if (selectedBulkRemarksOptions.length > 0) {
      const selectedTexts = selectedBulkRemarksOptions.map(option => 
        typeof option === 'string' ? option : option.inadequate_remarks
      );
      
      const currentText = bulkAdequacyRemarks || '';
      const existingRemarks = currentText.split(',').map(s => s.trim()).filter(Boolean);
      
      const newRemarks = [...new Set([...existingRemarks, ...selectedTexts])];
      const updatedText = newRemarks.join(', ');
      
      if (updatedText !== currentText) {
        setBulkAdequacyRemarks(updatedText);
      }
    }
  }, [selectedBulkRemarksOptions, bulkAdequacyRemarks]);

  const handleBulkAdequacySubmit = async () => {
    const currentAppId = getAppId();
    const currentOrgId = getOrgId();
    console.log('🔍 NonAnalyticalInstrumentsSection - handleBulkAdequacySubmit using orgId:', currentOrgId, 'appId:', currentAppId);

    if (!bulkAdequacyStatus) {
      setError("Please select a document status");
      return;
    }

    if (bulkAdequacyStatus === "Inadequate" && !bulkAdequacyRemarks?.trim()) {
      setError("Remarks are required when marking as Inadequate");
      return;
    }

    setBulkAdequacyLoading(true);
    try {
      // Determine NCtype based on userTypeId
      const userTypeId = localStorage.getItem('userTypeId');
      let ncType = 'Document Adequacy'; // Default
      
      if (userTypeId === '4') {
        ncType = 'Document Adequacy';
      } else if (userTypeId === '6') {
        ncType = 'Assessment';
      }
      
      console.log(`🔍 Bulk adequacy submit for userType ${userTypeId} with NCtype: ${ncType}`);
      
      const clauseNo = bulkSelectedClause
        ? (typeof bulkSelectedClause === 'string'
            ? bulkSelectedClause
            : (bulkSelectedClause.DisplayName || bulkSelectedClause.clause_no || bulkSelectedClause.text || ''))
        : null;

      const items = selectedAdequacyInstruments.map((instrumentId) => ({
        sourceid: instrumentId,
        informationStatus: bulkAdequacyStatus,
        observation: bulkAdequacyRemarks || "",
        NCtype: ncType,
        subsectionName: '',
        ClauseNo: clauseNo,
        isactive: 1,
      }));

      const bulkAssessorId = userTypeId === '6' ? assessmentId : 0;
      const bulkAssessmentId = userTypeId === '6' ? assessorId : (parseInt(localStorage.getItem('orgId')) || 0);

      await bulkUpsertDANC(
        currentOrgId,
        currentAppId,
        'Non-Analytical Instruments',
        bulkAssessorId,
        bulkAssessmentId,
        items
      );

      // Update local state for all selected instruments
      const newAdequacyChecks = { ...adequacyChecks };
      selectedAdequacyInstruments.forEach((instrumentId) => {
        newAdequacyChecks[instrumentId] = {
          status: bulkAdequacyStatus,
          remarks: bulkAdequacyRemarks || "",
          ClauseNo: bulkSelectedClause ? (typeof bulkSelectedClause === 'string' ? bulkSelectedClause : (bulkSelectedClause.DisplayName || bulkSelectedClause.clause_no || bulkSelectedClause.text || '')) : '',
          isSubmitted: true,
          submittedAt: new Date().toISOString(),
        };
      });
      setAdequacyChecks(newAdequacyChecks);

      setSuccess(`Bulk adequacy check submitted successfully for ${selectedAdequacyInstruments.length} instrument(s)!`);
      handleCloseBulkAdequacyModal();
      setSelectedAdequacyInstruments([]);
      
      // Reload DA-NC data
      await loadDANCData();
    } catch (error) {
      console.error("Error submitting bulk adequacy check:", error);
      setError(error.response?.data?.message || "Failed to submit bulk adequacy check");
    } finally {
      setBulkAdequacyLoading(false);
    }
  };

  const handleAdequacyInstrumentSelection = (instrumentId, isSelected) => {
    if (isSelected) {
      setSelectedAdequacyInstruments((prev) => [...prev, instrumentId]);
    } else {
      setSelectedAdequacyInstruments((prev) => prev.filter((id) => id !== instrumentId));
    }
  };

  const handleSelectAllAdequacyInstruments = (isSelected) => {
    if (isSelected) {
      setSelectedAdequacyInstruments(existingInstruments.map((inst) => inst.id));
    } else {
      setSelectedAdequacyInstruments([]);
    }
  };

  // Helper function to get adequacy status for an instrument
  const getInstrumentStatus = (instrumentId) => {
    const adequacy = adequacyChecks[String(instrumentId)]; // Ensure instrumentId is treated as string
    console.log(`🔍 Getting status for instrument ${instrumentId}:`, adequacy);
    return adequacy || {};
  };

  return (
    <Box p={3}>
      {/* Header */}
      <Box
        sx={{
          py: 2,
          px: 2,
          background: "#667eea",
          color: "white",
          borderRadius: 1,
          mb: 3,
        }}
      >
        <Typography variant="h6" sx={{ fontWeight: 600 }}>
          🧪 Non Analytical Instruments
        </Typography>
      </Box>

      {/* Alerts */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError("")}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess("")}>
          {success}
        </Alert>
      )}

      {/* Tabs for Add New and Existing Instruments */}
      <Paper sx={{ mb: 3 }}>
        <Tabs
          value={tabValue}
          onChange={handleTabChange}
          indicatorColor="primary"
          textColor="primary"
          sx={{ borderBottom: 1, borderColor: "divider" }}
        >
            {(applicationStage === null || applicationStage === "" || applicationStage === '18a' || applicationStage === '18' || applicationStage === '18b' || isSectionUnfrozen('Non-Analytical Instruments')) && <Tab label="Add New" sx={{ fontWeight: 600 }} />}
          <Tab label="Existing" sx={{ fontWeight: 600 }} />
        </Tabs>
      </Paper>

      {/* Tab Content */}
        {/* Tab 0: Add New Instrument Section */}
        {(applicationStage === null || applicationStage === "" || applicationStage === '18a' || applicationStage === '18' || applicationStage === '18b' || isSectionUnfrozen('Non-Analytical Instruments')) && tabValue === 0 && (
          <>
          <Paper sx={{ p: 3, mb: 3 }}>
            <Grid container spacing={3} alignItems="center">
              <Grid item xs={12} md={4}>
                <Autocomplete
                  fullWidth
                  options={availableInstruments}
                  getOptionLabel={(option) => option.InstrumentName || option}
                  value={
                    availableInstruments.find(
                      (instrument) =>
                        instrument.InstrumentName === selectedInstrument
                    ) || null
                  }
                  onChange={(event, newValue) => {
                    setSelectedInstrument(
                      newValue ? newValue.InstrumentName : ""
                    );
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Select Instrument"
                      placeholder="Search and select an instrument..."
                      variant="outlined"
                    />
                  )}
                  renderOption={(props, option) => (
                    <Box
                      component="li"
                      {...props}
                      key={option.InstrumentName || option.id}
                    >
                      {option.InstrumentName}
                    </Box>
                  )}
                  noOptionsText="No instruments found"
                  loading={false}
                  filterOptions={(options, { inputValue }) => {
                    return options.filter((option) =>
                      option.InstrumentName?.toLowerCase().includes(
                        inputValue.toLowerCase()
                      )
                    );
                  }}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <TextField
                  fullWidth
                  label="Quantity"
                  type="number"
                  value={quantity}
                  onChange={(e) => setQuantity(e.target.value)}
                  inputProps={{ min: 1 }}
                  placeholder="Enter quantity"
                />
              </Grid>
              {(applicationStage === null || applicationStage === "" || applicationStage === '18a' || isSectionUnfrozen('Non-Analytical Instruments')) && (
                <Grid item xs={12} md={3}>
                  <Button
                    variant="contained"
                    size="large"
                    onClick={handleAddInstrument}
                    disabled={!selectedInstrument || !quantity}
                    sx={{
                      height: "56px",
                      background:
                        "linear-gradient(135deg, #4caf50 0%, #45a049 100%)",
                      fontWeight: 600,
                      px: 3,
                      width: "100%",
                      "&:hover": {
                        background:
                          "linear-gradient(135deg, #45a049 0%, #3d8b40 100%)",
                      },
                      "&:disabled": {
                        background: "#cccccc",
                      },
                    }}
                  >
                    ➕ Add to List
                  </Button>
                </Grid>
              )}
            </Grid>
          </Paper>

          {/* Instruments Table (Add/Edit) */}
          <Paper sx={{ mb: 3 }}>
            {loading ? (
              <Box sx={{ display: "flex", justifyContent: "center", py: 5 }}>
                <CircularProgress />
              </Box>
            ) : (
              <TableContainer
                component={Paper}
                sx={{
                  boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
                  border: "1px solid #e0e0e0",
                }}
              >
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell
                        sx={{
                          fontWeight: 600,
                          color: "white",
                          borderRight: "1px solid rgba(255, 255, 255, 0.2)",
                          background: "#667eea"
                        }}
                      >
                        S No.
                      </TableCell>
                      <TableCell
                        sx={{
                          fontWeight: 600,
                          color: "white",
                          borderRight: "1px solid rgba(255, 255, 255, 0.2)",
                          background: "#667eea"
                        }}
                      >
                        Instrument Name
                      </TableCell>
                      <TableCell
                        sx={{
                          fontWeight: 600,
                          color: "white",
                          borderRight: "1px solid rgba(255, 255, 255, 0.2)",
                          background: "#667eea"
                        }}
                      >
                        UID
                      </TableCell>
                      <TableCell
                        sx={{
                          fontWeight: 600,
                          color: "white",
                          borderRight: "1px solid rgba(255, 255, 255, 0.2)",
                          background: "#667eea"
                        }}
                      >
                        Calibration Frequency
                      </TableCell>
                      <TableCell
                        sx={{
                          fontWeight: 600,
                          color: "white",
                          borderRight: "1px solid rgba(255, 255, 255, 0.2)",
                          background: "#667eea"
                        }}
                      >
                        Last Calibration Date
                      </TableCell>
                      <TableCell
                        sx={{
                          fontWeight: 600,
                          color: "white",
                          borderRight: "1px solid rgba(255, 255, 255, 0.2)",
                          background: "#667eea"
                        }}
                      >
                        Next Calibration Date
                      </TableCell>
                      <TableCell
                        sx={{
                          fontWeight: 600,
                          color: "white",
                          borderRight: "1px solid rgba(255, 255, 255, 0.2)",
                          background: "#667eea"
                        }}
                      >
                        Calibrated By
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {instruments.map((instrument, index) => (
                      <TableRow
                        key={instrument.id}
                        sx={{
                          "&:hover": { backgroundColor: "#f5f5f5" },
                          backgroundColor:
                            index % 2 === 0 ? "#ffffff" : "#f8f9fa",
                        }}
                      >
                        <TableCell sx={{ borderRight: "1px solid #e0e0e0" }}>
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            {index + 1}
                          </Typography>
                        </TableCell>
                        <TableCell sx={{ borderRight: "1px solid #e0e0e0" }}>
                          <Chip
                            label={instrument.name}
                            color="primary"
                            variant="outlined"
                            size="small"
                          />
                        </TableCell>
                        <TableCell sx={{ borderRight: "1px solid #e0e0e0" }}>
                          <TextField
                            size="small"
                            value={instrument.uid || ""}
                            onChange={(e) =>
                              handleInputChange(
                                instrument.id,
                                "uid",
                                e.target.value
                              )
                            }
                            placeholder="Enter UID"
                            fullWidth
                            sx={{ minWidth: 120 }}
                          />
                        </TableCell>
                        <TableCell sx={{ borderRight: "1px solid #e0e0e0" }}>
                          {instrument.calibratedReq === "Y" ? (
                            <TextField
                              size="small"
                              value={instrument.calibrationFrequency || ""}
                              onChange={(e) =>
                                handleInputChange(
                                  instrument.id,
                                  "calibrationFrequency",
                                  e.target.value
                                )
                              }
                              placeholder="Enter Calibration Frequency"
                              fullWidth
                              sx={{ minWidth: 150 }}
                            />
                          ) : (
                            "-"
                          )}
                        </TableCell>
                        <TableCell sx={{ borderRight: "1px solid #e0e0e0" }}>
                          {instrument.calibratedReq === "Y" ? (
                            <TextField
                              type="date"
                              size="small"
                              value={instrument.selectedDate1 || ""}
                              onChange={(e) =>
                                handleInputChange(
                                  instrument.id,
                                  "selectedDate1",
                                  e.target.value
                                )
                              }
                              fullWidth
                              sx={{ minWidth: 150 }}
                            />
                          ) : (
                            "-"
                          )}
                        </TableCell>
                        <TableCell sx={{ borderRight: "1px solid #e0e0e0" }}>
                          {instrument.calibratedReq === "Y" ? (
                            <TextField
                              type="date"
                              size="small"
                              value={instrument.selectedDate2 || ""}
                              onChange={(e) =>
                                handleInputChange(
                                  instrument.id,
                                  "selectedDate2",
                                  e.target.value
                                )
                              }
                              fullWidth
                              sx={{ minWidth: 150 }}
                            />
                          ) : (
                            "-"
                          )}
                        </TableCell>
                        <TableCell sx={{ borderRight: "1px solid #e0e0e0" }}>
                          {instrument.calibratedReq === "Y" ? (
                            <TextField
                              size="small"
                              value={instrument.calibratedBy || ""}
                              onChange={(e) =>
                                handleInputChange(
                                  instrument.id,
                                  "calibratedBy",
                                  e.target.value
                                )
                              }
                              placeholder="Enter Calibrated By"
                              fullWidth
                              sx={{ minWidth: 150 }}
                            />
                          ) : (
                            "-"
                          )}
                        </TableCell>     
                     </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Paper>
          {/* Save Button */}
          {(applicationStage === null || applicationStage === "" || applicationStage === '18a' || isSectionUnfrozen('Non-Analytical Instruments')) && (
            <Box sx={{ display: "flex", gap: 2, justifyContent: "flex-end" }}>
              {editId && (
              <Button
                variant="contained"
                size="large"
                onClick={() => {
                  setTabValue(1);
                  setInstruments([]);
                  setEditId(null);
                  setError("");
                  setSuccess("");
                  setLoading(false);
                }}
                disabled={loading || !instruments.length}
                sx={{
                  background: "#667eea",
                  color: "#fff",
                  px: 4,
                  py: 1.5,
                  fontWeight: 600,
                  fontSize: "1rem",
                  "&:hover": {
                    background:
                      "linear-gradient(135deg, #5a6fd8 0%, #6a4190 100%)",
                  },
                }}
              >
                Cancel Update
              </Button>
              )}
              <Button
                variant="contained"
                size="large"
                onClick={handleSaveToDatabase}
                disabled={loading || !instruments.length}
                startIcon={
                  loading ? <CircularProgress size={20} /> : <SaveIcon />
                }
                sx={{
                  background: "#667eea",
                  px: 4,
                  py: 1.5,
                  fontWeight: 600,
                  fontSize: "1rem",
                  "&:hover": {
                    background:
                      "linear-gradient(135deg, #5a6fd8 0%, #6a4190 100%)",
                  },
                }}
              >
                {loading
                  ? "Saving..."
                  : editId
                  ? "Update"
                  : "Save"}
              </Button>
            </Box>
          )}
          </>
        )}

        {/* Tab 1: Existing Instruments Section */}
        {(
          ((applicationStage === null || applicationStage === "" || applicationStage === '18a' || applicationStage === '18' || applicationStage === '18b' || isSectionUnfrozen('Non-Analytical Instruments')) && tabValue === 1) || 
          (applicationStage !== null && applicationStage !== "" && applicationStage !== '18' && applicationStage !== '18a' && applicationStage !== '18b' && tabValue === 0)
        ) && (
        <>
          {/* Bulk Actions for Adequacy Check */}
          {existingInstruments.length > 0 && (applicationStage === "18" || applicationStage === "18b" || applicationStage === "20") && (localStorage.getItem('userTypeId') === '4'|| localStorage.getItem('userTypeId') === '6') && (
            <Paper sx={{ p: 2, mb: 2 }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  Mark Adequacy Check:
                </Typography>
                <Checkbox
                  checked={selectedAdequacyInstruments.length === existingInstruments.length && existingInstruments.length > 0}
                  indeterminate={selectedAdequacyInstruments.length > 0 && selectedAdequacyInstruments.length < existingInstruments.length}
                  onChange={(e) => handleSelectAllAdequacyInstruments(e.target.checked)}
                />
                <Typography variant="body2" color="text.secondary">
                  Select All ({selectedAdequacyInstruments.length} selected)
                </Typography>
                <Button
                  variant="contained"
                  size="small"
                  onClick={handleOpenBulkAdequacyModal}
                  disabled={selectedAdequacyInstruments.length === 0}
                  startIcon={<AssignmentIcon />}
                  sx={{
                    ml: "auto",
                    background: "#667eea",
                  }}
                >
                  Bulk Adequacy Check
                </Button>
              </Box>
            </Paper>
          )}

          {/* Existing Instruments Table */}
          <Paper sx={{ mb: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, p: 2, pb: 0 }}>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                Existing Non-Analytical Instruments
              </Typography>
              {isSectionUnfrozen('Non-Analytical Instruments') && (
                <Chip
                  label="Unfrozen"
                  size="small"
                  color="warning"
                  sx={{ fontWeight: 600 }}
                />
              )}
            </Box>
            {isSectionUnfrozen('Non-Analytical Instruments') && (
              <Alert severity="warning" sx={{ m: 2, mt: 1 }}>
                This section is temporarily unfrozen for editing. You can add, edit, or delete instruments.
              </Alert>
            )}
            <TableContainer
              component={Paper}
              sx={{
                boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
                border: "1px solid #e0e0e0",
                 maxHeight: 600,
                overflow: "auto",
              }}
            >
              <Table  stickyHeader>
                <TableHead>
                  <TableRow>
                    {(applicationStage === "18" || applicationStage === "18b" || applicationStage === "20") && (localStorage.getItem('userTypeId') === '4'|| localStorage.getItem('userTypeId') === '6') && (
                      <TableCell
                        sx={{
                          fontWeight: 600,
                          color: "white",
                          borderRight: "1px solid rgba(255, 255, 255, 0.2)",
                          background: "#667eea"
                        }}
                      >
                        <Checkbox
                          checked={selectedAdequacyInstruments.length === existingInstruments.length && existingInstruments.length > 0}
                          indeterminate={selectedAdequacyInstruments.length > 0 && selectedAdequacyInstruments.length < existingInstruments.length}
                          onChange={(e) => handleSelectAllAdequacyInstruments(e.target.checked)}
                          sx={{ color: "white" }}
                        />
                      </TableCell>
                    )}
                    <TableCell
                      sx={{
                        fontWeight: 600,
                        color: "white",
                        borderRight: "1px solid rgba(255, 255, 255, 0.2)",
                        background: "#667eea"
                      }}
                    >
                      S No.
                    </TableCell>
                    <TableCell
                      sx={{
                        fontWeight: 600,
                        color: "white",
                        borderRight: "1px solid rgba(255, 255, 255, 0.2)",
                        background: "#667eea"
                      }}
                    >
                      Instrument Name
                    </TableCell>
                    <TableCell
                      sx={{
                        fontWeight: 600,
                        color: "white",
                        borderRight: "1px solid rgba(255, 255, 255, 0.2)",
                        background: "#667eea"
                      }}
                    >
                      UID
                    </TableCell>
                    <TableCell
                      sx={{
                        fontWeight: 600,
                        color: "white",
                        borderRight: "1px solid rgba(255, 255, 255, 0.2)",
                        background: "#667eea"
                      }}
                    >
                      Calibration Frequency
                    </TableCell>
                    <TableCell
                      sx={{
                        fontWeight: 600,
                        color: "white",
                        borderRight: "1px solid rgba(255, 255, 255, 0.2)",
                        background: "#667eea"
                      }}
                    >
                      Last Calibration Date
                    </TableCell>
                    <TableCell
                      sx={{
                        fontWeight: 600,
                        color: "white",
                        borderRight: "1px solid rgba(255, 255, 255, 0.2)",
                        background: "#667eea"
                      }}
                    >
                      Next Calibration Date
                    </TableCell>
                    <TableCell
                      sx={{
                        fontWeight: 600,
                        color: "white",
                        borderRight: "1px solid rgba(255, 255, 255, 0.2)",
                        background: "#667eea"
                      }}
                    >
                      Calibrated By
                    </TableCell>
                    {(applicationStage === "18" || applicationStage === "18a" || applicationStage === "18b" || applicationStage === "20") && (
                      <TableCell
                        sx={{
                          fontWeight: 600,
                          color: "white",
                          borderRight: "1px solid rgba(255, 255, 255, 0.2)",
                          background: "#667eea"
                        }}
                      >
                        Adequacy Status
                      </TableCell>
                    )}
                    {(applicationStage === null || applicationStage === "18a" || isSectionUnfrozen('Non-Analytical Instruments')) && (
                    <TableCell sx={{ fontWeight: 600, color: "white", background: "#667eea" }}>
                      Actions
                    </TableCell>
                    )}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {existingInstruments.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={(applicationStage === "18" || applicationStage === "18a" || applicationStage === "18b" || applicationStage === "20") ? 10 : 8} align="center">
                        No existing instruments found.
                      </TableCell>
                    </TableRow>
                  ) : (
                    existingInstruments.map((inst, idx) => {
                      const statusObj = getInstrumentStatus(inst.id);
                      const status = statusObj.status || "";
                      console.log(`🔍 Instrument ${idx + 1}: ID=${inst.id}, Name=${inst.InstrumentName}, Status=${status}`);
                      return (
                        <TableRow
                          key={inst.id || idx}
                          sx={{
                            backgroundColor:
                              idx % 2 === 0 ? "#ffffff" : "#f8f9fa",
                          }}
                        >
                          {(applicationStage === "18" || applicationStage === "18b" || applicationStage === "20") && (localStorage.getItem('userTypeId') === '4'|| localStorage.getItem('userTypeId') === '6') && (
                            <TableCell>
                              <Checkbox
                                checked={selectedAdequacyInstruments.includes(inst.id)}
                                onChange={(e) => handleAdequacyInstrumentSelection(inst.id, e.target.checked)}
                              />
                            </TableCell>
                          )}
                          <TableCell>{idx + 1}</TableCell>
                          <TableCell>
                            <Chip
                              label={inst.InstrumentName}
                              color="primary"
                              variant="outlined"
                              size="small"
                            />
                          </TableCell>
                          <TableCell>{inst.UID || "-"}</TableCell>
                          <TableCell>
                            {inst.Calibration_Frequency || "-"}
                          </TableCell>
                          <TableCell>
                            {inst.LastCalibration_Date
                              ? new Date(
                                  inst.LastCalibration_Date
                                ).toLocaleDateString()
                              : "-"}
                          </TableCell>
                          <TableCell>
                            {inst.NextCalibration_Date
                              ? new Date(
                                  inst.NextCalibration_Date
                                ).toLocaleDateString()
                              : "-"}
                          </TableCell>
                          <TableCell>
                            {inst.CalibratedBy || "-"}
                          </TableCell>
                          {(applicationStage === "18" || applicationStage === "18a" || applicationStage === "18b" || applicationStage === "20") && (
                            <TableCell>
                              <Box sx={{ display: "flex", alignItems: "center", flexDirection: 'column', gap: 0.5 }}>
                                <Box
                                  sx={{
                                    px: 2,
                                    py: 0.5,
                                    borderRadius: 1,
                                    fontSize: '0.75rem',
                                    fontWeight: 600,
                                    textAlign: 'center',
                                    minWidth: '80px',
                                    bgcolor: status === 'Adequate' ? '#e8f5e8' :
                                      status === 'Inadequate' ? '#ffebee' :
                                      status === 'To be verify' ? '#fff3cd' : '#f5f5f5',
                                    color: status === 'Adequate' ? '#2e7d32' :
                                      status === 'Inadequate' ? '#c62828' :
                                      status === 'To be verify' ? '#856404' : '#666',
                                    border: `1px solid ${status === 'Adequate' ? '#a5d6a7' :
                                      status === 'Inadequate' ? '#ef9a9a' :
                                      status === 'To be verify' ? '#ffeaa7' : '#ddd'
                                    }`
                                  }}
                                >
                                  {status || 'Not Assessed'}
                                </Box>
                                {status === "Inadequate" && adequacyChecks[String(inst.id)]?.remarks && (
                                  <Typography variant="caption" sx={{ color: '#e62300ff', fontStyle: 'italic' }}>
                                    "{adequacyChecks[String(inst.id)]?.remarks}"
                                  </Typography>
                                )}
                                {adequacyChecks[String(inst.id)]?.ClauseNo && (
                                  <Chip
                                    label={`Clause: ${adequacyChecks[String(inst.id)]?.ClauseNo}`}
                                    size="small"
                                    sx={{
                                      fontSize: '0.625rem',
                                      height: 20,
                                      bgcolor: '#e3f2fd',
                                      color: '#1976d2',
                                      border: '1px solid #bbdefb'
                                    }}
                                  />
                                )}
                              </Box>
                            </TableCell>
                          )}
                          {(applicationStage === null || applicationStage === "" || applicationStage === "18a" || isSectionUnfrozen('Non-Analytical Instruments')) && (
                          <TableCell>
                            <Tooltip title="Edit">
                              <IconButton
                                size="small"
                                onClick={() => handleEditExistingInstrument(inst)}
                                sx={{
                                  color: "#1976d2",
                                  "&:hover": { backgroundColor: "#e3f2fd" },
                                }}
                              >
                                <EditIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Delete">
                              <IconButton
                                size="small"
                                onClick={() => handleDeleteExistingInstrument(inst)}
                                sx={{
                                  color: "#d32f2f",
                                  "&:hover": { backgroundColor: "#ffebee" },
                                }}
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </TableCell>
                          )}
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </>
        )}

      {/* Delete Existing Confirmation Dialog */}
      <Dialog
        open={deleteExistingDialogOpen}
        onClose={cancelDeleteExisting}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle
          sx={{
            background: "linear-gradient(135deg, #ff6b6b 0%, #ee5a24 100%)",
            color: "white",
          }}
        >
          Confirm Delete
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          <Typography variant="body1">
            Are you sure you want to delete this existing instrument? This
            action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button
            onClick={cancelDeleteExisting}
            variant="outlined"
            sx={{ px: 3 }}
          >
            Cancel
          </Button>
          <Button
            onClick={confirmDeleteExisting}
            variant="contained"
            color="error"
            sx={{ px: 3 }}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Adequacy Check Modal */}
      <AdequacyCheckPopup
        open={adequacyModalOpen}
        onClose={handleCloseAdequacyModal}
        instrumentId={currentAdequacyInstrumentId}
        instrumentName={currentAdequacyInstrumentName}
        adequacyCheck={adequacyChecks[currentAdequacyInstrumentId]}
        onAdequacyChange={handleAdequacyChange}
        onRemarksChange={handleAdequacyRemarks}
        onSubmit={handleAdequacySubmit}
        loading={adequacyLoading}
        clauseNo={clauseNo}
        selectedClause={selectedClause}
        onClauseChange={setSelectedClause}
        userTypeId={userTypeId}
      />

      {/* Bulk Adequacy Check Modal */}
      <Dialog
        open={bulkAdequacyModalOpen}
        onClose={handleCloseBulkAdequacyModal}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: { borderRadius: 3, boxShadow: "0 20px 40px rgba(0,0,0,0.1)" },
        }}
      >
        <DialogTitle
          sx={{
            background: "#667eea",
            color: "white",
            fontWeight: 600,
            fontSize: "1.25rem",
          }}
        >
          📋 Bulk Adequacy Check - {selectedAdequacyInstruments.length} Instrument(s)
        </DialogTitle>

        <DialogContent sx={{ p: 3, mt: 2 }}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <FormControl
                fullWidth
                sx={{
                  "& .MuiOutlinedInput-root": {
                    backgroundColor: "white",
                    borderRadius: 2,
                    "&:hover": {
                      boxShadow: "0 2px 8px rgba(0,123,255,0.15)",
                    },
                    "&.Mui-focused": {
                      boxShadow: "0 4px 12px rgba(0,123,255,0.25)",
                    },
                  },
                }}
              >
                <InputLabel>Document Status</InputLabel>
                <Select
                  value={bulkAdequacyStatus}
                  onChange={(e) => setBulkAdequacyStatus(e.target.value)}
                  label="Document Status"
                >
                  <MenuItem value="">-- Select Status --</MenuItem>
                  <MenuItem value="Adequate">
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Box
                        sx={{
                          width: 8,
                          height: 8,
                          borderRadius: "50%",
                          bgcolor: "#28a745",
                        }}
                      />
                      Adequate
                    </Box>
                  </MenuItem>
                  <MenuItem value="Inadequate">
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Box
                        sx={{
                          width: 8,
                          height: 8,
                          borderRadius: "50%",
                          bgcolor: "#dc3545",
                        }}
                      />
                      Inadequate
                    </Box>
                  </MenuItem>
                  {userTypeId === '4' && (
                  <MenuItem value="To be verify">
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Box
                        sx={{
                          width: 8,
                          height: 8,
                          borderRadius: "50%",
                          bgcolor: "#35c3dcff",
                        }}
                      />
                      To be verify
                    </Box>
                  </MenuItem>
                  )}
                </Select>
              </FormControl>
            </Grid>

            {(bulkAdequacyStatus === 'Inadequate' && userTypeId === '6') && (
              <Grid item xs={12} md={6}>
                <Autocomplete
                  fullWidth
                  options={clauseNo}
                  value={bulkSelectedClause}
                  onChange={(event, newValue) => {
                    setBulkSelectedClause(newValue);
                  }}
                  getOptionLabel={(option) => {
                    if (!option) return '';
                    if (typeof option === 'string') return option;
                    return option.DisplayName || option.clause_no || option.text || '';
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Clause No."
                      variant="outlined"
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          backgroundColor: 'white',
                          borderRadius: 2,
                          '&:hover': {
                            boxShadow: '0 2px 8px rgba(0,123,255,0.15)',
                          },
                          '&.Mui-focused': {
                            boxShadow: '0 4px 12px rgba(0,123,255,0.25)',
                          },
                        },
                      }}
                    />
                  )}
                  sx={{ mb: 2 }}
                />
              </Grid>
            )}

            {bulkAdequacyStatus === "Inadequate" && (
              <Grid item xs={12}>
                {/* Simple textarea for remarks */}
                <TextField
                  fullWidth
                  multiline
                  rows={4}
                  label="Remarks (Required)"
                  placeholder="Type your detailed remarks here. Use the dropdown below to select predefined remarks."
                  value={bulkAdequacyRemarks || ''}
                  onChange={(e) => handleBulkAdequacyRemarksChange(e.target.value, false)}
                  required
                  variant="outlined"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      backgroundColor: 'white',
                      borderRadius: 2,
                      '&:hover': {
                        boxShadow: '0 2px 8px rgba(220,53,69,0.15)',
                      },
                      '&.Mui-focused': {
                        boxShadow: '0 4px 12px rgba(220,53,69,0.25)',
                      },
                    },
                  }}
                />
                
                {/* Predefined Remarks Dropdown */}
                {bulkScrutinyRemarks.length > 0 && (
                  <Box sx={{ mt: 2, position: 'relative' }}>
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={() => setIsBulkCheckboxDropdownOpen(!isBulkCheckboxDropdownOpen)}
                      sx={{ 
                        mb: 1,
                        borderColor: '#007bff',
                        color: '#007bff',
                        '&:hover': { borderColor: '#0056b3', backgroundColor: '#f8f9ff' }
                      }}
                    >
                      {isBulkCheckboxDropdownOpen ? 'Hide' : 'Show'} Predefined Remarks ({bulkScrutinyRemarks.length} available)
                    </Button>
                    
                    {selectedBulkRemarksOptions.length > 0 && (
                      <Button
                        variant="text"
                        size="small"
                        onClick={handleClearAllBulkCheckboxes}
                        sx={{ ml: 1, color: '#dc3545' }}
                      >
                        Clear All ({selectedBulkRemarksOptions.length})
                      </Button>
                    )}

                    {isBulkCheckboxDropdownOpen && (
                      <Paper
                        elevation={3}
                        sx={{
                          mt: 1,
                          maxHeight: 300,
                          overflow: 'auto',
                          border: '1px solid #e0e0e0',
                          borderRadius: 2
                        }}
                      >
                        <MenuList dense>
                          {bulkScrutinyRemarks.map((option, index) => {
                            const remarkText = typeof option === 'string' ? option : option.inadequate_remarks;
                            const isSelected = selectedBulkRemarksOptions.some(
                              item => (typeof item === 'string' ? item : item.inadequate_remarks) === remarkText
                            );
                            
                            return (
                              <MenuItem
                                key={index}
                                onClick={() => handleBulkCheckboxChange(option, !isSelected)}
                                sx={{
                                  py: 1,
                                  px: 2,
                                  display: 'flex',
                                  alignItems: 'flex-start',
                                  minHeight: 'auto'
                                }}
                              >
                                <Checkbox
                                  checked={isSelected}
                                  size="small"
                                  sx={{ p: 0, mr: 2 }}
                                />
                                <Box sx={{ flexGrow: 1 }}>
                                  <Typography
                                    variant="body2"
                                    sx={{
                                      fontSize: '0.875rem',
                                      lineHeight: 1.4,
                                      wordBreak: 'break-word',
                                      whiteSpace: 'pre-wrap'
                                    }}
                                  >
                                    {remarkText}
                                  </Typography>
                                  {typeof option !== 'string' && option.type && (
                                    <Chip
                                      label={option.type}
                                      size="small"
                                      color={option.type === 'Assessment' ? 'primary' : 'secondary'}
                                      variant="outlined"
                                      sx={{ 
                                        fontSize: '0.7rem', 
                                        height: '18px',
                                        mt: 0.5
                                      }}
                                    />
                                  )}
                                </Box>
                              </MenuItem>
                            );
                          })}
                        </MenuList>
                      </Paper>
                    )}
                  </Box>
                )}
                
                <Typography variant="caption" sx={{
                  color: '#6c757d',
                  display: 'block',
                  mt: 1,
                  fontStyle: 'italic'
                }}>
                  {bulkLoadingRemarks
                    ? "Loading existing remarks..."
                    : bulkScrutinyRemarks.length > 0
                      ? `${bulkScrutinyRemarks.length} predefined remark(s) available for "Non-Analytical Instruments". You can select multiple options from the dropdown above.`
                      : "Type your detailed remarks in the text area above."
                  }
                </Typography>
              </Grid>
            )}
          </Grid>

          {bulkAdequacyStatus && (
            <Box sx={{ mt: 3 }}>
              <Alert
                severity={bulkAdequacyStatus === "Adequate" ? "success" : "warning"}
                sx={{ borderRadius: 2 }}
              >
                {bulkAdequacyStatus === "Adequate"
                  ? `✓ ${selectedAdequacyInstruments.length} instrument(s) will be marked as Adequate.`
                  : `⚠ ${selectedAdequacyInstruments.length} instrument(s) will be marked as Inadequate.`}
              </Alert>
            </Box>
          )}
        </DialogContent>

        <DialogActions sx={{ p: 3, pt: 0 }}>
          <Button onClick={handleCloseBulkAdequacyModal} sx={{ color: "#6c757d" }}>
            Cancel
          </Button>
          {bulkAdequacyStatus && (
            <Button
              variant="contained"
              onClick={handleBulkAdequacySubmit}
              disabled={
                bulkAdequacyLoading ||
                (bulkAdequacyStatus === "Inadequate" && !bulkAdequacyRemarks?.trim())
              }
              sx={{
                background:
                  bulkAdequacyStatus === "Adequate"
                    ? "linear-gradient(135deg, #28a745 0%, #20c997 100%)"
                    : "linear-gradient(135deg, #ffc107 0%, #fd7e14 100%)",
                color: "white",
                fontWeight: 600,
                "&:hover": {
                  transform: "translateY(-1px)",
                  boxShadow: "0 4px 12px rgba(0,0,0,0.2)",
                },
              }}
            >
              {bulkAdequacyLoading ? (
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <CircularProgress size={16} sx={{ color: "white" }} />
                  Submitting...
                </Box>
              ) : (
                "📤 Submit Bulk Adequacy Check"
              )}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={!!success || !!error}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={success ? "success" : "error"}
          sx={{ width: "100%" }}
        >
          {success || error}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default NonAnalyticalInstrumentsSection;
